import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Typography,
  Card,
  CardContent,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  FormGroup,
  InputAdornment,
  IconButton,
  Skeleton
} from '@mui/material';
import { motion } from 'framer-motion';
import { FaChevronRight, FaUsers, FaArrowLeft, FaImage } from 'react-icons/fa';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { LoginSignupState } from 'types/loginSignUpTypes';
import type { AppDispatch } from 'app/store';
import { joinCommunity, clearToast, verifySignupOtp } from 'store/slices/loginSignupSlice';

interface JoinCommunityProps {
  onBack: () => void;
  initialGroupId?: string;
  colors: {
    primary: string;
    secondary: string;
    textSecondary: string;
    textPrimary: string;
  };
}

interface SelectedCommunity {
  group_id: string;
  label: string;
  description: string;
  avatar?: string;
  peopleCount?: number;
}

const steps = ['Select Community', 'Complete Registration', 'Verify Code'];

const JoinCommunity: React.FC<JoinCommunityProps> = ({ onBack, colors, initialGroupId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [selectedCommunity, setSelectedCommunity] = useState<SelectedCommunity | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const [submitError, setSubmitError] = useState('');
  const [isSubmittingJoin, setIsSubmittingJoin] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    surburb: '',
    state: '',
    country_res: '',
    postcode: '',
    password: '',
    confirmPassword: '',
    agreeTerms: false,
    groups: [] as string[]
  });

  const { loading, groups, toast } = useSelector((state: any) => state.loginSignup || {});

  const groupsList = groups || [];

  // Auto-select group when navigating from landing page "Join" button
  useEffect(() => {
    if (!initialGroupId || !groupsList.length) return;
    const match = groupsList.find((g: any) => g.group_id === initialGroupId);
    if (match && !selectedGroup) {
      setSelectedGroup(match.group_id);
      setSelectedCommunity({
        group_id: match.group_id,
        label: match.label,
        description: match.description,
        avatar: match.avatar,
        peopleCount: match.peopleCount
      });
      setActiveStep(1);
    }
  }, [initialGroupId, groupsList.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredGroups = groupsList.filter((group: any) =>
    group.label?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    group.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleImageError = (groupId: string, avatarUrl: string) => {
    console.error(`❌ Failed to load image for group ${groupId}:`, avatarUrl);
    console.log('Transformed URL:', getImageUrl(avatarUrl));
    setFailedImages(prev => new Set(prev).add(groupId));
  };

  // Transform avatar URL - Try using the original Azurite URL with SAS token
  const getImageUrl = (avatarUrl: string) => {
    if (!avatarUrl) return '';
    
    console.log('🔄 Processing avatar URL:', avatarUrl);
    
    // First, try using the original Azurite URL as-is (it has SAS token for auth)
    // If CORS is configured on Azurite, this should work
    if (avatarUrl.includes('127.0.0.1:10000') || avatarUrl.includes('devstoreaccount1')) {
      console.log('✅ Using Azurite URL directly:', avatarUrl);
      return avatarUrl;
    }
    
    // If using a different storage or static server
    if (process.env.REACT_APP_STATIC_BASE_URL && avatarUrl.includes('devstoreaccount1')) {
      try {
        const url = new URL(avatarUrl);
        const match = url.pathname.match(/\/devstoreaccount1\/(.+)/);
        if (match) {
          const staticBaseUrl = process.env.REACT_APP_STATIC_BASE_URL;
          const transformedUrl = `${staticBaseUrl}/${match[1]}`;
          console.log('🔄 Transformed to static server:', transformedUrl);
          return transformedUrl;
        }
      } catch (error) {
        console.error('❌ Error parsing avatar URL:', error);
      }
    }
    
    // Return original URL as fallback
    console.log('➡️ Using original URL:', avatarUrl);
    return avatarUrl;
  };

  const handleSelectCommunity = (group: any) => {
    console.log('✅ Selected community:', group);
    setSelectedGroup(group.group_id);
    setSelectedCommunity({
      group_id: group.group_id,
      label: group.label,
      description: group.description,
      avatar: group.avatar,
      peopleCount: group.peopleCount
    });
    // Move to next step immediately after selection
    setTimeout(() => setActiveStep(1), 200);
  };



  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmitForm = () => {
    // Validate form data
    if (!formData.fullName || !formData.email || !formData.password || !formData.phone) {
      setSubmitError('Please fill in all required fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setSubmitError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setSubmitError('Password must be at least 8 characters');
      return;
    }

    if (!formData.agreeTerms) {
      setSubmitError('You must agree to the terms and conditions');
      return;
    }

    if (!selectedCommunity) {
      setSubmitError('Please select a community');
      return;
    }

    setSubmitError('');
    setIsSubmittingJoin(true);

    // Prepare user data with groups
    const userData = {
      fullName: formData.fullName,
      email: formData.email,
      password: formData.password,
      phone: formData.phone,
      surburb: formData.surburb,
      state: formData.state,
      country_res: formData.country_res,
      postcode: formData.postcode,
      groups: [selectedCommunity.group_id]
    };

    console.log('📤 Submitting join community:', userData);

    // Dispatch joinCommunity action
    dispatch(joinCommunity(userData) as any).then((result: any) => {
      if (result.payload && !result.error) {
        // Success - move to OTP verification
        setActiveStep(2);
      } else {
        // Error is already shown via toast
        setSubmitError(result.payload || 'Failed to join community');
      }
      setIsSubmittingJoin(false);
    }).catch(() => {
      setSubmitError('Failed to join community');
      setIsSubmittingJoin(false);
    });
  };

  const handleVerifyOTP = () => {
    if (!otpCode || otpCode.length !== 6) {
      setSubmitError('Please enter a valid 6-digit OTP');
      return;
    }

    console.log('🔐 Verifying Signup OTP:', otpCode);
    // Prevent full page refresh and only show one toast
    dispatch(verifySignupOtp({ username: formData.email, otp: otpCode }) as any).then(
      (result: any) => {
        if (result.meta.requestStatus === 'fulfilled') {
          // Success - OTP verified, clear form and state, navigate to services
          setTimeout(() => {
            setFormData({
              fullName: '',
              email: '',
              phone: '',
              surburb: '',
              state: '',
              country_res: '',
              postcode: '',
              password: '',
              confirmPassword: '',
              agreeTerms: false,
              groups: []
            });
            setOtpCode('');
            dispatch({ type: 'loginSignup/resetSignupState' });
            navigate('/services');
          }, 1500);
        } else {
          // Error - OTP verification failed, stay on OTP step, show only one toast
          setSubmitError(result.payload || 'Failed to verify OTP');
        }
      }
    );
  };

  // OTP input handlers
  const handleOtpChange = (e: any, index: number) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) && value.length <= 1) {
      const newCode = otpCode.split('');
      newCode[index] = value;
      const joinedCode = newCode.join('');
      setOtpCode(joinedCode);
      setSubmitError(''); // Clear error on input change
      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-input-join-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleOtpKeyDown = (e: any, index: number) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      const prevInput = document.getElementById(`otp-input-join-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    } else {
      onBack();
    }
  };

  return (
    <Box sx={{ width: '100%', maxHeight: '480px', overflowY: 'auto' }}>
      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 1, '& .MuiStepLabel-label': { fontSize: '12px' } }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step 1: Select Community */}
      {activeStep === 0 && (
        <Box>
          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              onClick={handleBack}
              sx={{ minWidth: 'auto', color: colors.primary }}
            >
              <FaArrowLeft size={16} />
            </Button>
            <Typography variant="h6" sx={{ fontWeight: 600, color: colors.textPrimary }}>
              Choose your community
            </Typography>
          </Box>

          {/* Search Box */}
          <TextField
            fullWidth
            placeholder="Search here"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ mb: 1.5 }}
          />

          {(loading || groupsList.length === 0) ? (
            <Box sx={{ height: '340px', pr: 1 }}>
              <Grid container spacing={1.5}>
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <Grid item xs={6} key={index}>
                    <Card sx={{ p: 1.5 }}>
                      <Skeleton variant="rectangular" width="100%" height={100} sx={{ borderRadius: '8px', mb: 1 }} />
                      <Skeleton variant="text" width="80%" height={20} sx={{ mb: 0.5 }} />
                      <Skeleton variant="text" width="60%" height={16} />
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : (
            <Box sx={{
              height: '340px',
              overflowY: 'auto',
              overflowX: 'hidden',
              pr: 1,
              pb: 4,
              scrollBehavior: 'smooth',
              '&::-webkit-scrollbar': { width: '8px' },
              '&::-webkit-scrollbar-track': { background: '#F5F5F5', borderRadius: '8px' },
              '&::-webkit-scrollbar-thumb': { 
                background: '#C4C4C7', 
                borderRadius: '8px',
                '&:hover': { background: '#A4A4A7' }
              }
            }}>
              {filteredGroups.length > 0 ? (
                <Grid container spacing={1.5} sx={{ pb: 2 }}>
                  {filteredGroups.map((group: any, index: number) => (
                      <Grid item xs={6} key={group.group_id || index}>
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          whileHover={{ scale: 1.02 }}
                        >
                          <Card
                            onClick={() => handleSelectCommunity(group)}
                            sx={{
                              cursor: 'pointer',
                              border: selectedGroup === group.group_id ? `2px solid ${colors.primary}` : `1px solid #E4E4E7`,
                              background: selectedGroup === group.group_id ? `${colors.primary}15` : '#FFFFFF',
                              transition: 'all 0.2s',
                              p: 1.5,
                              '&:hover': {
                                borderColor: colors.primary,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                              }
                            }}
                          >
                            {!failedImages.has(group.group_id) && group.avatar ? (
                              <Box
                                component="img"
                                src={getImageUrl(group.avatar)}
                                alt={group.label}
                                onError={(e) => {
                                  console.error('❌ Image failed to load:', {
                                    groupId: group.group_id,
                                    groupLabel: group.label,
                                    originalUrl: group.avatar,
                                    transformedUrl: getImageUrl(group.avatar),
                                    error: e
                                  });
                                  handleImageError(group.group_id, group.avatar);
                                }}
                                onLoad={() => {
                                  console.log('✅ Image loaded successfully:', group.label);
                                }}
                                sx={{
                                  width: '100%',
                                  height: '100px',
                                  borderRadius: '8px',
                                  objectFit: 'cover',
                                  mb: 1
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: '100%',
                                  height: '100px',
                                  borderRadius: '8px',
                                  mb: 1,
                                  background: `${colors.primary}20`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <FaImage size={32} color={colors.primary} opacity={0.5} />
                              </Box>
                            )}
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                              {group.label}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                              <FaUsers size={14} color={colors.textSecondary} />
                              <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                                {group.peopleCount || 0} Members
                              </Typography>
                            </Box>
                          </Card>
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  height: '200px'
                }}>
                  <Alert severity="info" sx={{ width: '100%' }}>No communities found. Try a different search.</Alert>
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Display Toast Messages */}
      {toast && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <Alert
            severity={toast.severity}
            onClose={() => dispatch(clearToast())}
            sx={{ mb: 2, borderRadius: 1, fontSize: '0.9rem' }}
          >
            {toast.message}
          </Alert>
        </motion.div>
      )}

      {/* Display Error Messages */}
      {submitError && (
        <Alert severity="error" onClose={() => setSubmitError('')} sx={{ mb: 2, borderRadius: 1 }}>
          {submitError}
        </Alert>
      )}

      {/* Step 2: Registration Form */}
      {activeStep === 1 && selectedCommunity && (
        <Box sx={{ maxHeight: '360px', overflowY: 'auto', pr: 1 }}>
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 0.5, fontSize: '0.85rem' }}>
              You've been automatically assigned to the <strong>{selectedCommunity.label}</strong> community level for your selected group.
            </Typography>

            <TextField
              fullWidth
              label="Full Name"
              placeholder="Enter your full name"
              value={formData.fullName}
              onChange={(e) => handleFormChange('fullName', e.target.value)}
              size="small"
              sx={{ mb: 0.75 }}
            />

            <TextField
              fullWidth
              label="Email Address"
              placeholder="Enter your email"
              type="email"
              value={formData.email}
              onChange={(e) => handleFormChange('email', e.target.value)}
              size="small"
              sx={{ mb: 0.75 }}
            />

            <TextField
              fullWidth
              label="Phone Number"
              placeholder="Enter your phone number"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleFormChange('phone', e.target.value)}
              size="small"
              sx={{ mb: 0.75 }}
            />

            <Grid container spacing={1} sx={{ mb: 0.75 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Suburb"
                  placeholder="Enter suburb"
                  value={formData.surburb}
                  onChange={(e) => handleFormChange('surburb', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="State"
                  placeholder="Enter state"
                  value={formData.state}
                  onChange={(e) => handleFormChange('state', e.target.value)}
                  size="small"
                />
              </Grid>
            </Grid>

            <Grid container spacing={1} sx={{ mb: 0.75 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Country"
                  placeholder="Enter country"
                  value={formData.country_res}
                  onChange={(e) => handleFormChange('country_res', e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Postcode"
                  placeholder="Enter postcode"
                  value={formData.postcode}
                  onChange={(e) => handleFormChange('postcode', e.target.value)}
                  size="small"
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Enter Password"
              placeholder="Create a password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => handleFormChange('password', e.target.value)}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ mb: 0.75 }}
            />

            <TextField
              fullWidth
              label="Confirm Password"
              placeholder="Confirm your password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={(e) => handleFormChange('confirmPassword', e.target.value)}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ mb: 0.75 }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
              <Checkbox
                checked={formData.agreeTerms}
                onChange={(e) => handleFormChange('agreeTerms', e.target.checked)}
                size="small"
              />
              <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                I agree with the <span style={{ color: colors.primary }}>Terms & Conditions</span> of Utrust
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleBack}
              disabled={loading || isSubmittingJoin}
              sx={{ borderColor: colors.primary, color: colors.primary }}
            >
              Back
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmitForm}
              disabled={loading || isSubmittingJoin || !formData.fullName || !formData.email || !formData.phone || !formData.surburb || !formData.state || !formData.country_res || !formData.postcode || !formData.password || !formData.agreeTerms || !selectedCommunity}
              sx={{
                background: colors.primary,
                color: '#FFFFFF',
                '&:disabled': { background: '#E4E4E7' }
              }}
            >
              {isSubmittingJoin ? <CircularProgress size={20} sx={{ color: '#FFFFFF' }} /> : 'Get OTP'}
            </Button>
          </Box>
        </Box>
      )}

      {/* Step 3: OTP Verification */}
      {activeStep === 2 && (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: colors.textPrimary }}>
            Enter OTP
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: colors.textSecondary }}>
            Verification code has been sent to {formData.email}
          </Typography>

          {/* OTP Input Fields - 6 Boxes */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, justifyContent: 'center' }}>
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <TextField
                key={index}
                id={`otp-input-join-${index}`}
                value={otpCode[index] || ''}
                onChange={(e: any) => handleOtpChange(e, index)}
                onKeyDown={(e: any) => handleOtpKeyDown(e, index)}
                inputProps={{
                  maxLength: 1,
                  style: {
                    textAlign: 'center',
                    fontSize: '1.5rem',
                    fontWeight: 'bold'
                  }
                }}
                sx={{
                  width: '50px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    '& input': {
                      padding: '16px 8px'
                    }
                  }
                }}
              />
            ))}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Typography variant="caption" sx={{ color: colors.textSecondary }}>
              Didn't receive a code?{' '}
              <span style={{ color: colors.primary, cursor: 'pointer', fontWeight: 600 }}>
                Resend
              </span>
            </Typography>
          </Box>

          {/* Display Error Messages */}
          {submitError && (
            <Alert severity="error" onClose={() => setSubmitError('')} sx={{ mb: 2, borderRadius: 1 }}>
              {submitError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleBack}
              disabled={loading}
              sx={{ borderColor: colors.primary, color: colors.primary }}
            >
              Back
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleVerifyOTP}
              disabled={loading || otpCode.length < 6}
              sx={{
                background: colors.primary,
                color: '#FFFFFF',
                '&:disabled': { background: '#E4E4E7' }
              }}
            >
              {loading ? <CircularProgress size={24} /> : 'Verify'}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default JoinCommunity;
